import {
    Component,
    inject,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy
} from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { EntityType } from "~app/common/models/constants";
import { EntityDTO } from "~app/common/models/entity-models";
import { RaskCommonModule } from "~app/common/rask-common.module";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { canEdit } from "~app/role-permissions";
import { Atgard, SkogsvardEntity } from "../../models/atgard-models";
import {
    IVKAtgardKedjaKopplingMasshanteringsResponseDTO,
    VKAtgardKedjaKopplingCopyGeometryDisplay,
    VKAtgardKedjaKopplingCopyGeometryDTO
} from "../../models/Atgardskedjor/atgardskedjor-models";
import { SkogsvardSelectAtgardItem } from "../../models/skogsvard/createskogsvard-models";
import { AtgardKedjaMediatorService } from "../../services/atgardkedja-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { SkogsvardSelectAtgardComponent } from "../../skogsvard/skogsvard-create-dialog/skogsvard-select-atgard/skogsvard-select-atgard.component";
import { VKAtgardHelper } from "../../utils/vkatgard-helper";
@Component({
    selector: "rask-atgard-kedja-copy-geometry-dialog",
    templateUrl: "./atgard-kedja-copy-geometry-dialog.component.html",
    styleUrl: "./atgard-kedja-copy-geometry-dialog.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RaskCommonModule,
        SkogsvardSelectAtgardComponent
    ]
})
export class AtgardKedjaCopyGeometryDialogComponent implements OnInit, OnDestroy {
    modal = inject(NgbActiveModal);

    protected copyFromAtgardList: SkogsvardSelectAtgardItem[] = [];
    protected atgardListToCopyTo: VKAtgardKedjaKopplingCopyGeometryDisplay[] = [];
    protected preSelectedAtgard?: string;

    private atgarderPreload: EntityDTO[];
    private kontraktAtgarder: Atgard[];
    private kedjaKopplingar: IVKAtgardKedjaKopplingMasshanteringsResponseDTO[];
    private copyFrom: string;
    private kontraktReadonly = false;
    private kedjeGuid: string;
    private readonlyAtgardGuids: string[];
    private rundvirkeAtgardGuids: string[];
    private readonly atgardKedjaMediatorService = inject(AtgardKedjaMediatorService);
    private readonly busyService = inject(BusyService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngUnsubscribe$ = new Subject<void>();

    private readonly canEdit = canEdit();

    init(kedjeGuid: string, atgarderPreload: EntityDTO[], kontraktAtgarder: Atgard[]): void {
        this.kedjeGuid = kedjeGuid;
        this.atgarderPreload = atgarderPreload;
        this.kontraktAtgarder = kontraktAtgarder;
        this.readonlyAtgardGuids = this.kontraktAtgarder.filter(a => a.isReadonly).map(a => a.guid);
        this.rundvirkeAtgardGuids = this.kontraktAtgarder.filter(a => !a.isSkogsvard).map(a => a.guid);
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.busyService.addWork(this.atgardKedjaMediatorService.getMasshanteraKedjaKopplingarByKedjaGuid(this.kedjeGuid))
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(kedjaKopplingar => {
                this.kedjaKopplingar = kedjaKopplingar;
                this.setAtgarderLists();
            });

        this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit()),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(readonly => this.kontraktReadonly = readonly);
    }

    protected copyFromAtgardChanged(item: SkogsvardSelectAtgardItem): void {
        this.atgardListToCopyTo.forEach((atg) => {
            atg.checked = false;
            atg.isSourceAtgard = atg.atgardGuid === item?.value;
        });

        this.copyFrom = item?.value;
    }

    protected canCopy(): boolean {
        if (ObjectUtils.isNullOrUndefined(this.kedjaKopplingar)) {
            return false;
        }

        const anyselected = this.atgardListToCopyTo.some(a => a.checked);
        return !this.kontraktReadonly && this.copyFrom && anyselected;
    }

    protected close(): void {
        this.modal.close();
    }

    protected copy(): void {
        const dto: VKAtgardKedjaKopplingCopyGeometryDTO = {
            copyAtgardGuid: this.copyFrom,
            copyToAtgarderList: this.atgardListToCopyTo.filter(a => a.checked).map(a => a.atgardGuid)
        };

        this.modal.close(dto);
    }

    private setAtgarderLists(): void {
        this.setCopyToList();

        const listOfAtgarder: SkogsvardSelectAtgardItem[] = [];
        this.getAtgarderforSelect(listOfAtgarder);
        this.getSkogsvardAtgarderforSelect(listOfAtgarder);
        this.copyFromAtgardList = listOfAtgarder;
    }

    private getAtgarderforSelect(list: SkogsvardSelectAtgardItem[]) {
        let index = 0;
        this.kontraktAtgarder.forEach((atg) => {
            const entity = this.atgarderPreload.find(x => x.guid === atg.guid);
            if (entity?.type !== EntityType.virkeskopsAtgardSkogsvard && atg.geometri && atg.nettoareal) {
                if (this.kedjaKopplingar.some(k => k.atgardGuid === atg.guid)) {
                    list.push(this.createSkogsvardSelectFromAtgard(atg, index, atg.avverkningsTyp?.grupp, false));
                }
                index++;
            }
        });
    }

    private getSkogsvardAtgarderforSelect(list: SkogsvardSelectAtgardItem[]) {
        const skogsvardLista = this.atgarderPreload.filter(x => VKAtgardHelper.isAtgardEntitySkogsvard(x)) as SkogsvardEntity[];
        const skogsvardListSorted = this.atgardKedjaMediatorService.getSorteradAtgardslista(skogsvardLista);

        let index = 0;
        skogsvardListSorted.forEach((entity) => {
            const atg = this.kontraktAtgarder.find(x => x.guid === entity.guid);

            if (atg.geometri && atg.nettoareal && this.kedjaKopplingar.some(k => k.atgardGuid === atg.guid)) {
                list.push(this.createSkogsvardSelectFromAtgard(atg, index, atg.atgardstyp, true));
            }
            index++;
        });
    }

    private createSkogsvardSelectFromAtgard(atg: Atgard, index: number, grupp: string, isSkogsvard: boolean): SkogsvardSelectAtgardItem {
        return new SkogsvardSelectAtgardItem(
            null,
            null,
            atg.guid,
            grupp,
            `${atg.beteckning} - ${atg.nettoareal} ha`,
            index,
            isSkogsvard
        );
    }

    private setCopyToList() {
        const rvList = this.rundvirkeAtgardGuids.filter(aRv => this.kedjaKopplingar.some(kk => kk.atgardGuid === aRv));
        if (rvList.length > 0) {
            this.preSelectedAtgard = rvList[0];
        }

        this.kedjaKopplingar.forEach((kedjekoppling) => {
            if (!this.rundvirkeAtgardGuids.some(g => g === kedjekoppling.atgardGuid)) {
                const isReadonlyAtgard = this.readonlyAtgardGuids.some(g => g === kedjekoppling.atgardGuid);
                const isAtgardOnThisKontrakt = this.kontraktAtgarder.some(a => a.guid === kedjekoppling.atgardGuid);

                const atgardCheckable: VKAtgardKedjaKopplingCopyGeometryDisplay = {
                    atgardGuid: kedjekoppling.atgardGuid,
                    atgardstyp: kedjekoppling.atgardstyp,
                    beteckning: kedjekoppling.beteckning,
                    status: kedjekoppling.status,
                    checked: false,
                    isSourceAtgard: false,
                    isReadonly: isReadonlyAtgard || !isAtgardOnThisKontrakt
                };
                this.atgardListToCopyTo.push(atgardCheckable);
            }
        });
    }
}
